import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Clock, ArrowLeft, Play, Pause, RotateCcw, Check,
  Trophy, TrendingUp, Loader2, BookOpen, Timer, Volume2
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const passages = [
  {
    id: 'passage-1',
    title: 'The Happy Dog',
    level: 1,
    text: 'The dog ran fast. He saw a ball. The ball was red. The dog got the ball. He was happy. The dog ran home.',
    wordCount: 28
  },
  {
    id: 'passage-2',
    title: 'My Cat',
    level: 1,
    text: 'I have a cat. My cat is soft. She likes to nap. She naps on my bed. I love my cat. My cat loves me.',
    wordCount: 28
  },
  {
    id: 'passage-3',
    title: 'At School',
    level: 2,
    text: 'I go to school every day. I learn to read and write. My teacher is nice. She helps me with my work. I have many friends at school. We play at recess. School is fun.',
    wordCount: 40
  },
  {
    id: 'passage-4',
    title: 'The Farm',
    level: 2,
    text: 'The farm has many animals. There are cows and pigs. The chickens lay eggs. The farmer feeds them every morning. The big red barn keeps them safe. I like visiting the farm.',
    wordCount: 38
  },
  {
    id: 'passage-5',
    title: 'A Rainy Day',
    level: 3,
    text: 'Rain was falling from the gray clouds. The children could not play outside. They decided to build a fort with blankets and chairs. Inside the fort, they read stories and played games. When the rain stopped, a beautiful rainbow appeared in the sky.',
    wordCount: 50
  }
];

const TimedReadingPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [childData, setChildData] = useState(null);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [errors, setErrors] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [wpm, setWpm] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  useEffect(() => {
    if (isReading && !isPaused) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isReading, isPaused]);

  const fetchChildData = async () => {
    try {
      const response = await api.get(`/reading/assessment/${childId}`);
      setChildData(response.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startReading = () => {
    setIsReading(true);
    setElapsedTime(0);
    setErrors(0);
    setShowResults(false);
    setWpm(null);
  };

  const pauseReading = () => {
    setIsPaused(true);
  };

  const resumeReading = () => {
    setIsPaused(false);
  };

  const finishReading = async () => {
    setIsReading(false);
    setIsPaused(false);
    clearInterval(timerRef.current);
    
    // Calculate WPM
    const minutes = elapsedTime / 60;
    const wordsRead = selectedPassage.wordCount - errors;
    const calculatedWpm = Math.round(wordsRead / minutes);
    setWpm(calculatedWpm);
    setShowResults(true);

    // Submit to API
    try {
      await api.post(`/reading/timed-reading/${childId}?words_read=${selectedPassage.wordCount}&errors=${errors}&time_seconds=${elapsedTime}&passage_id=${selectedPassage.id}`);
      toast.success('Reading recorded!');
      fetchChildData();
    } catch (error) {
      toast.error('Failed to save results');
    }
  };

  const resetReading = () => {
    setIsReading(false);
    setIsPaused(false);
    setElapsedTime(0);
    setErrors(0);
    setShowResults(false);
    setWpm(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const speakPassage = () => {
    if (selectedPassage) {
      _speakTTS(selectedPassage.text, 0.9);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Timer className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800">Timed Reading</h1>
                  <p className="text-sm text-slate-500">
                    Current WPM: {childData?.current_scores?.timed_reading_wpm || '--'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!selectedPassage ? (
          <>
            {/* Passage Selection */}
            <h2 className="text-2xl font-bold mb-6">Choose a Passage</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {passages.map(passage => (
                <Card 
                  key={passage.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:border-green-300"
                  onClick={() => setSelectedPassage(passage)}
                  data-testid={`passage-${passage.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{passage.title}</h3>
                        <p className="text-sm text-slate-500">Level {passage.level}</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        {passage.wordCount} words
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm line-clamp-2">{passage.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Previous Results */}
            {childData?.assessment_history?.filter(a => a.assessment_type === 'timed_reading').length > 0 && (
              <Card className="mt-8">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Your Reading History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {childData.assessment_history
                      .filter(a => a.assessment_type === 'timed_reading')
                      .slice(0, 5)
                      .map((assessment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{assessment.score} WPM</p>
                            <p className="text-sm text-slate-500">
                              {assessment.details?.words_read || 0} words, {assessment.details?.errors || 0} errors
                            </p>
                          </div>
                          <span className="text-sm text-slate-400">
                            {new Date(assessment.assessed_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : showResults ? (
          /* Results */
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="p-8">
              <Trophy className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">Great Reading!</h2>
              
              <div className="grid grid-cols-3 gap-4 my-8">
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-3xl font-bold text-green-600">{wpm}</p>
                  <p className="text-sm text-green-700">WPM</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-3xl font-bold text-blue-600">{formatTime(elapsedTime)}</p>
                  <p className="text-sm text-blue-700">Time</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <p className="text-3xl font-bold text-amber-600">{errors}</p>
                  <p className="text-sm text-amber-700">Errors</p>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => {
                  resetReading();
                  setSelectedPassage(null);
                }}>
                  Choose Another
                </Button>
                <Button onClick={resetReading} className="bg-green-600 hover:bg-green-700">
                  Read Again
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Reading Mode */
          <div className="max-w-2xl mx-auto">
            {/* Timer Bar */}
            <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-xl shadow-sm">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  isReading ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
                }`}>
                  <span className="text-2xl font-bold">{formatTime(elapsedTime)}</span>
                </div>
                <div>
                  <p className="font-medium">{selectedPassage.title}</p>
                  <p className="text-sm text-slate-500">{selectedPassage.wordCount} words</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                {!isReading ? (
                  <Button onClick={startReading} className="bg-green-600 hover:bg-green-700" data-testid="start-btn">
                    <Play className="w-4 h-4 mr-2" /> Start
                  </Button>
                ) : isPaused ? (
                  <Button onClick={resumeReading} className="bg-green-600 hover:bg-green-700">
                    <Play className="w-4 h-4 mr-2" /> Resume
                  </Button>
                ) : (
                  <Button onClick={pauseReading} variant="outline">
                    <Pause className="w-4 h-4 mr-2" /> Pause
                  </Button>
                )}
                <Button variant="ghost" onClick={() => {
                  resetReading();
                  setSelectedPassage(null);
                }}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Passage */}
            <Card>
              <CardContent className="p-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-xl">{selectedPassage.title}</h3>
                  <Button variant="ghost" size="sm" onClick={speakPassage}>
                    <Volume2 className="w-4 h-4 mr-1" /> Listen
                  </Button>
                </div>
                
                <p className="text-2xl leading-relaxed text-slate-700" data-testid="passage-text">
                  {selectedPassage.text}
                </p>

                {/* Error Counter */}
                {isReading && (
                  <div className="mt-8 pt-6 border-t">
                    <Label className="text-sm text-slate-500 mb-2 block">
                      Mark errors while reading (tap each time there's an error)
                    </Label>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => setErrors(prev => prev + 1)}
                        className="flex-1"
                        data-testid="error-btn"
                      >
                        Error ({errors})
                      </Button>
                      {errors > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setErrors(prev => Math.max(0, prev - 1))}
                        >
                          Undo
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Finish Button */}
            {isReading && (
              <div className="mt-6 text-center">
                <Button 
                  size="lg" 
                  onClick={finishReading}
                  className="bg-green-600 hover:bg-green-700 px-12"
                  data-testid="finish-btn"
                >
                  <Check className="w-5 h-5 mr-2" /> Finish Reading
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TimedReadingPage;
